import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { FrequencyBadge } from '@/components/shared/FrequencyBadge'
import { useBasePath } from '@/lib/paths'
import { ComplianceFilingSummary } from '@/types'
import { FileText, Plus, AlertCircle } from 'lucide-react'


type Props = {
  filings: ComplianceFilingSummary[]
  search: string
  pagination: PaginationMeta
}

export default function ComplianceFilingsIndex({ filings, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title="Compliance Filings" breadcrumbs={[{ label: 'Compliance Filings' }]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Compliance Filings</h1>
          <p className="text-sm text-muted-foreground">Manage your recurring compliance filings</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/compliance_filings/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Filing
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search filings..." />
        </div>

        {filings.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <FileText className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No compliance filings yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Start tracking your first compliance filing</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/compliance_filings/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {filings.map((filing, i) => (
              <Link
                key={filing.id}
                href={`${basePath}/compliance_filings/${filing.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{filing.type_name}</span>
                      <StatusBadge status={filing.status} />
                      <FrequencyBadge frequency={filing.frequency} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{filing.business_name}</span>
                      {filing.next_due_date && (
                        <>
                          <span>·</span>
                          <span className={filing.next_due_date && new Date(filing.next_due_date) < new Date() ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                            {filing.next_due_date && new Date(filing.next_due_date) < new Date() && <AlertCircle className="w-3 h-3" />}
                            Due: {new Date(filing.next_due_date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {filing.latest_record_stage && <StageBadge stage={filing.latest_record_stage} variant="compliance" />}
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
